import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  Plus, FileText, Eye, Pencil, Trash2, Send, Clock,
  Loader2, CheckCircle, Calendar, Hash, EyeOff, ToggleLeft, ToggleRight, AlertTriangle
} from "lucide-react";
import ArticleEditor from "./ArticleEditor";
import { useExpertArticles } from "../../hooks/useArticles";

export default function ExpertArticlesTab({ userId }) {
  const { articles, loading, saveArticle, deleteArticle, toggleHideArticle } = useExpertArticles(userId);
  const navigate = useNavigate();
  const [editingArticle, setEditingArticle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async (data) => {
    setSaving(true);
    const result = await saveArticle(data);
    setSaving(false);
    if (result) {
      setEditingArticle(null);
      setIsCreating(false);
    }
    return result;
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    await deleteArticle(id);
    setDeleting(false);
    setConfirmDeleteId(null);
  };

  const handleToggleHide = async (article) => {
    await toggleHideArticle(article.id, !!article.hidden_at);
  };

  if (isCreating || editingArticle) {
    return (
      <ArticleEditor
        article={editingArticle}
        onSave={handleSave}
        onCancel={() => { setEditingArticle(null); setIsCreating(false); }}
        saving={saving}
        expertId={userId}
      />
    );
  }

  const publishedArticles = articles.filter((a) => a.status === "published" && !a.hidden_at);
  const draftArticles = articles.filter((a) => a.status === "draft");
  const hiddenArticles = articles.filter((a) => !!a.hidden_at);

  const filteredArticles = activeFilter === "published"
    ? publishedArticles
    : activeFilter === "drafts"
    ? draftArticles
    : activeFilter === "hidden"
    ? hiddenArticles
    : articles;

  const filters = [
    { id: "all", label: "Все", count: articles.length },
    { id: "published", label: "Опубликовано", count: publishedArticles.length },
    { id: "drafts", label: "Черновики", count: draftArticles.length },
    { id: "hidden", label: "Скрытые", count: hiddenArticles.length }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-medium text-white">Мои статьи</h2>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-5 py-2 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
        >
          <Plus className="w-4 h-4" />
          Новая статья
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-md bg-neutral-900 border border-neutral-800">
          <div className="flex items-center gap-2 mb-2">
            <Send className="w-5 h-5 text-accent-400" />
            <span className="text-lg font-extralight text-neutral-400">Опубликовано</span>
          </div>
          <p className="text-2xl font-medium text-white">{publishedArticles.length}</p>
        </div>
        <div className="p-5 rounded-md bg-neutral-900 border border-neutral-800">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-yellow-400" />
            <span className="text-lg font-extralight text-neutral-400">Черновики</span>
          </div>
          <p className="text-2xl font-medium text-white">{draftArticles.length}</p>
        </div>
        <div className="p-5 rounded-md bg-neutral-900 border border-neutral-800">
          <div className="flex items-center gap-2 mb-2">
            <EyeOff className="w-5 h-5 text-neutral-500" />
            <span className="text-lg font-extralight text-neutral-400">Скрытые</span>
          </div>
          <p className="text-2xl font-medium text-white">{hiddenArticles.length}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveFilter(f.id)}
            className={`px-4 py-1.5 rounded-md text-lg font-extralight transition-colors ${
              activeFilter === f.id
                ? "bg-primary-600 text-white"
                : "bg-neutral-800/50 text-neutral-400 hover:text-white hover:bg-neutral-800"
            }`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-lg font-extralight text-neutral-500">Загрузка статей...</p>
        </div>
      ) : filteredArticles.length === 0 ? (
        <div className="p-6 rounded-md bg-neutral-900 border border-neutral-800 text-center py-12">
          <FileText className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
          <h3 className="text-xl font-light text-neutral-200 mb-2">
            {articles.length === 0 ? "Пока нет статей" : "Нет статей в этой категории"}
          </h3>
          <p className="text-lg font-extralight text-neutral-500 mb-6 max-w-md mx-auto">
            {articles.length === 0
              ? "Пишите статьи, чтобы привлекать клиентов через Google и Яндекс. Статьи автоматически оптимизируются для поисковых систем."
              : "Попробуйте выбрать другую категорию."}
          </p>
          {articles.length === 0 && (
            <button
              onClick={() => setIsCreating(true)}
              className="px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
            >
              Написать первую статью
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredArticles.map((article) => (
            <div
              key={article.id}
              onClick={() => {
                if (article.status === "published" && article.slug && !article.hidden_at) {
                  navigate(`/articles/${article.slug}`);
                } else {
                  setEditingArticle(article);
                }
              }}
              className={`p-4 rounded-md bg-neutral-900 border transition-colors cursor-pointer hover:border-primary-700/40 ${
                article.hidden_at
                  ? "border-neutral-800/50 opacity-70"
                  : "border-neutral-800"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-md text-lg font-extralight ${
                      article.hidden_at
                        ? "bg-neutral-700/30 text-neutral-500"
                        : article.status === "published"
                        ? "bg-accent-500/10 text-accent-400"
                        : "bg-yellow-500/10 text-yellow-400"
                    }`}>
                      {article.hidden_at
                        ? "Скрыто"
                        : article.status === "published"
                        ? "Опубликовано"
                        : "Черновик"}
                    </span>
                    {article.published_at && !article.hidden_at && (
                      <span className="text-lg font-extralight text-neutral-500">
                        {new Date(article.published_at).toLocaleDateString("ru-RU")}
                      </span>
                    )}
                    {article.status === "draft" && article.updated_at && (
                      <span className="flex items-center gap-1 text-lg font-extralight text-neutral-500">
                        <Clock className="w-3.5 h-3.5" />
                        Сохранено {new Date(article.updated_at).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" })}{" "}
                        в {new Date(article.updated_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                    {article.hidden_at && (
                      <span className="text-lg font-extralight text-neutral-600">
                        Скрыто {new Date(article.hidden_at).toLocaleDateString("ru-RU")}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-medium text-white mb-1 truncate">
                    {article.title || "Без заголовка"}
                  </h3>
                  <p className="text-lg font-extralight text-neutral-500 truncate">
                    {article.excerpt || "Без описания"}
                  </p>
                  {(article.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {article.tags.slice(0, 4).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-neutral-800/50 text-neutral-500 text-lg font-extralight"
                        >
                          <Hash className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  {article.status === "published" && !article.hidden_at && (
                    <div className="flex items-center gap-3 text-lg font-extralight text-neutral-500">
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        <span>{article.views_count || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{article.reading_time} мин</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    {article.status === "published" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleHide(article); }}
                        className={`p-2 rounded-md transition-colors ${
                          article.hidden_at
                            ? "hover:bg-accent-500/10 text-neutral-500 hover:text-accent-400"
                            : "hover:bg-yellow-500/10 text-neutral-500 hover:text-yellow-400"
                        }`}
                        title={article.hidden_at ? "Показать статью" : "Скрыть статью"}
                      >
                        {article.hidden_at ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingArticle(article); }}
                      className="p-2 rounded-md hover:bg-primary-500/10 text-neutral-500 hover:text-primary-400 transition-colors"
                      title="Редактировать"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(article.id); }}
                      className="p-2 rounded-md hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {confirmDeleteId === article.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 pt-3 border-t border-neutral-800 flex items-center justify-between flex-wrap gap-3">
                      <div onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 text-yellow-400">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span className="text-lg font-extralight">
                          Удалить статью "{article.title || "Без заголовка"}"? Это действие нельзя отменить.
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          disabled={deleting}
                          className="px-4 py-1.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white hover:border-neutral-500 text-lg font-extralight transition-colors"
                        >
                          Отмена
                        </button>
                        <button
                          onClick={() => handleDelete(article.id)}
                          disabled={deleting}
                          className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-red-600 hover:bg-red-500 text-white text-lg font-extralight transition-colors disabled:opacity-50"
                        >
                          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          Удалить
                        </button>
                      </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}